import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Percent, Loader2, Save } from "lucide-react";

const SEGMENTS = [
  { key: "residenziale", label: "Residenziale", hint: "Target: Residenziale" },
  { key: "ci", label: "C&I (Commerciale & Industriale)", hint: "Target: Commerciale" },
  { key: "utility", label: "Utility", hint: "Target: Industriale" },
];

export default function MarginiCosts() {
  const [m, setM] = useState({ residenziale: 30, ci: 25, utility: 22, residenziale_min: 30, ci_min: 25, utility_min: 22 });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings/margini").then((r) => setM({
      residenziale: r.data.residenziale ?? 30,
      ci: r.data.ci ?? 25,
      utility: r.data.utility ?? 22,
      residenziale_min: r.data.residenziale_min ?? r.data.residenziale ?? 30,
      ci_min: r.data.ci_min ?? r.data.ci ?? 25,
      utility_min: r.data.utility_min ?? r.data.utility ?? 22,
    })).finally(() => setLoading(false));
  }, []);

  const save = async () => {
    const payload = {
      residenziale: Number(m.residenziale), ci: Number(m.ci), utility: Number(m.utility),
      residenziale_min: Number(m.residenziale_min), ci_min: Number(m.ci_min), utility_min: Number(m.utility_min),
    };
    if (Object.values(payload).some((v) => Number.isNaN(v) || v < 0 || v >= 100)) {
      toast.error("Inserisci margini validi (0–99%)");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.put("/settings/margini", payload);
      setM({
        residenziale: data.residenziale, ci: data.ci, utility: data.utility,
        residenziale_min: data.residenziale_min, ci_min: data.ci_min, utility_min: data.utility_min,
      });
      toast.success("Margini salvati");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <header className="sticky top-14 lg:top-0 z-10 h-16 border-b border-border bg-background flex items-center justify-between px-4 lg:px-8">
        <div>
          <h1 className="text-xl font-heading font-bold">Margini</h1>
          <p className="text-xs text-muted-foreground">Margine di default per segmento, applicato al prezzo suggerito in base al Target del lead</p>
        </div>
        <Button onClick={save} disabled={saving || loading} data-testid="save-margini-btn" className="font-semibold">
          {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />} Salva margini
        </Button>
      </header>

      <div className="p-4 lg:p-8">
        {loading ? (
          <div className="flex h-40 items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
        ) : (
          <Card className="rounded border-border p-5 max-w-xl" data-testid="margini-card">
            <h2 className="text-sm uppercase tracking-[0.15em] text-muted-foreground font-bold mb-4 flex items-center gap-2">
              <Percent className="h-4 w-4 text-primary" strokeWidth={1.5} /> Margini per segmento (%)
            </h2>
            <p className="text-xs text-muted-foreground mb-5">Il prezzo mostrato nel preventivo e al partner è calcolato sul <b>Margine minimo</b> del segmento: <b>costo commerciale ÷ (1 − margine minimo)</b>. Il segmento è determinato dal Target del lead; il tecnico può comunque sovrascrivere il prezzo finale a mano.</p>
            <div className="grid grid-cols-[1fr_auto_auto] items-center gap-x-4 mb-2 text-[10px] uppercase tracking-wide text-muted-foreground">
              <div></div>
              <div className="w-24 text-center">Default</div>
              <div className="w-24 text-center text-primary">Minimo</div>
            </div>
            <div className="space-y-4">
              {SEGMENTS.map((s) => (
                <div key={s.key} className="grid grid-cols-[1fr_auto_auto] items-center gap-x-4" data-testid={`margine-row-${s.key}`}>
                  <div>
                    <div className="text-sm font-medium">{s.label}</div>
                    <div className="text-[11px] text-muted-foreground">{s.hint}</div>
                  </div>
                  <div className="relative w-24">
                    <Input type="number" min={0} max={99} step="0.5" value={m[s.key]} onChange={(e) => setM((p) => ({ ...p, [s.key]: e.target.value }))} data-testid={`margine-${s.key}-input`} className="pr-7" />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">%</span>
                  </div>
                  <div className="relative w-24">
                    <Input type="number" min={0} max={99} step="0.5" value={m[`${s.key}_min`]} onChange={(e) => setM((p) => ({ ...p, [`${s.key}_min`]: e.target.value }))} data-testid={`margine-min-${s.key}-input`} className="pr-7 border-primary/50" />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">%</span>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}
